import { sampleOnly } from '../../lib/sample';
// Made-up conversations for judging the design (see lib/sample.ts). Real ones will come from the mailbox and WhatsApp.
export type PartyKey = 'Customer' | 'Overseas Agent' | 'Accountant';
export const PARTIES: PartyKey[] = ['Customer', 'Overseas Agent', 'Accountant'];

export type Attachment = { name: string; size: string };
export type Message = {
  id: string;
  /** "30 May 2025": messages with the same date sit under one date line */
  date: string;
  time: string;
  /** who wrote it; 'You' for us */
  from: string;
  text: string;
  attachments?: Attachment[];
  /** a note for yourself, never sent to anyone */
  internal?: boolean;
};

export type Thread = { contact: string; status: string; messages: Message[] };
export type NextAction = { text: string; owner: string; due: string };

export type Conversation = {
  srb: string;
  summary: string;
  last: string;
  unread: number;
  followUp: boolean;
  ai: string;
  threads: Record<PartyKey, Thread>;
  next: NextAction[];
};

/** One line in the activity feed: something came in, or something is waiting */
export type ActivityStatus = 'New Update' | 'Needs Attention' | 'Awaiting Reply' | 'Updated' | 'Pending Approval';
export type ActivityEvent = {
  id: string;
  /** "23 Jul 2025" */
  date: string;
  time: string;
  srb: string;
  party: PartyKey;
  /** the company or person on the other side */
  org: string;
  subject: string;
  text: string;
  status: ActivityStatus;
};

/** The sample "today" and "yesterday" the feed is grouped by */
export const SAMPLE_TODAY = '23 Jul 2025';
export const SAMPLE_YESTERDAY = '22 Jul 2025';

export const EVENTS: ActivityEvent[] = sampleOnly([
  {
    id: 'e1',
    date: '23 Jul 2025',
    time: '11:30 AM',
    srb: 'SRB-711',
    party: 'Customer',
    org: 'ABC Trading',
    subject: 'Approve customer quotation',
    text: 'Customer asked for the quotation. Your approval is needed before it goes out.',
    status: 'Needs Attention',
  },
  {
    id: 'e2',
    date: '23 Jul 2025',
    time: '10:45 AM',
    srb: 'SRB-711',
    party: 'Overseas Agent',
    org: 'EK Logistics',
    subject: 'Air freight rate received',
    text: 'Agent provided air freight rate:\n• USD 6.23/kg (TT 4–5 days)\n• Alternative: USD 5.70/kg (TT 7 days)',
    status: 'New Update',
  },
  {
    id: 'e3',
    date: '23 Jul 2025',
    time: '09:30 AM',
    srb: 'SRB-596',
    party: 'Customer',
    org: 'Horizon Co.',
    subject: 'Confirm side-loader availability',
    text: 'Customer requested confirmation of side-loader arrangements.',
    status: 'Needs Attention',
  },
  {
    id: 'e4',
    date: '23 Jul 2025',
    time: '09:15 AM',
    srb: 'SRB-539',
    party: 'Accountant',
    org: 'Sokaina',
    subject: 'Follow up on final agent invoice',
    text: 'Requested final agent invoice to prepare JCS.',
    status: 'Awaiting Reply',
  },
  {
    id: 'e5',
    date: '23 Jul 2025',
    time: '08:50 AM',
    srb: 'SRB-640',
    party: 'Customer',
    org: 'Al-Rafedi',
    subject: 'Customer feedback on rates',
    text: 'Customer approved the updated dimensions and requested final pricing.',
    status: 'New Update',
  },
  {
    id: 'e6',
    date: '22 Jul 2025',
    time: '04:20 PM',
    srb: 'SRB-625',
    party: 'Overseas Agent',
    org: 'Mersin Forwarding',
    subject: 'Container gate-in confirmation',
    text: 'Confirmed pickup completed and cargo received at their warehouse.',
    status: 'Updated',
  },
  {
    id: 'e7',
    date: '22 Jul 2025',
    time: '03:10 PM',
    srb: 'SRB-648',
    party: 'Customer',
    org: 'TechnoPack',
    subject: 'Agent confirmation on RJ rate',
    text: 'Shared packing list and confirmed 14 pallets ready for pickup.',
    status: 'New Update',
  },
  {
    id: 'e8',
    date: '22 Jul 2025',
    time: '01:45 PM',
    srb: 'SRB-518',
    party: 'Overseas Agent',
    org: 'Valencia Logistics',
    subject: 'DG approval update',
    text: 'DG approval still pending. Expect update by 25 July.',
    status: 'Awaiting Reply',
  },
  {
    id: 'e9',
    date: '22 Jul 2025',
    time: '12:30 PM',
    srb: 'SRB-625',
    party: 'Accountant',
    org: 'Sokaina',
    subject: 'Payment to supplier',
    text: 'Draft supplier payment ready. It needs your approval.',
    status: 'Pending Approval',
  },
  {
    id: 'e10',
    date: '22 Jul 2025',
    time: '11:20 AM',
    srb: 'SRB-539',
    party: 'Customer',
    org: 'TechnoPack',
    subject: 'Customer update message',
    text: 'Customer acknowledged the revised schedule and confirmed to proceed.',
    status: 'Updated',
  },
  {
    id: 'e11',
    date: '21 Jul 2025',
    time: '05:30 PM',
    srb: 'SRB-596',
    party: 'Accountant',
    org: 'Sokaina',
    subject: 'Customer quotation draft',
    text: 'Prepared draft supplier payment for approval.',
    status: 'Pending Approval',
  },
  {
    id: 'e12',
    date: '21 Jul 2025',
    time: '04:00 PM',
    srb: 'SRB-518',
    party: 'Overseas Agent',
    org: 'Valencia Logistics',
    subject: 'Follow up on DG approval',
    text: 'Chase the DG approval; the customer is waiting.',
    status: 'Needs Attention',
  },
  {
    id: 'e13',
    date: '21 Jul 2025',
    time: '02:10 PM',
    srb: 'SRB-711',
    party: 'Overseas Agent',
    org: 'MU Logistics',
    subject: 'Agent response on alternative rate',
    text: 'Requested additional details on cargo description.',
    status: 'Awaiting Reply',
  },
]);

/** A conversation with no hand-written messages gets its messages from the feed, so the two always agree */
function withFeed(c: Conversation): Conversation {
  const threads = { ...c.threads };
  for (const p of PARTIES) {
    const mine = EVENTS.filter((e) => e.srb === c.srb && e.party === p);
    if (threads[p].messages.length || !mine.length) continue;
    threads[p] = {
      contact: `Main contact: ${mine[0]!.org}`,
      status: mine[0]!.subject,
      messages: [...mine].reverse().map((e) => ({ id: e.id, date: e.date, time: e.time, from: e.org, text: e.text })),
    };
  }
  return { ...c, threads };
}

const none = (status: string): Thread => ({ contact: '', status, messages: [] });
const bare = (srb: string, summary: string, last: string, unread: number, followUp: boolean): Conversation => ({
  srb,
  summary,
  last,
  unread,
  followUp,
  ai: summary,
  threads: {
    Customer: none('No messages yet'),
    'Overseas Agent': none('No messages yet'),
    Accountant: none('No messages yet'),
  },
  next: [],
});

const BASE: Conversation[] = sampleOnly([
  {
    srb: 'SRB-539',
    summary: 'Agent confirmed new ETD. Customer updated. Awaiting final agent invoice.',
    last: '16 Jun 2025',
    unread: 3,
    followUp: true,
    ai: 'The overseas agent confirmed that the shipment was rolled over from Antwerp to a new departure on 3 June. The customer has been informed and confirmed to proceed. The accountant is awaiting the final agent invoice.',
    threads: {
      Customer: {
        contact: 'Main contact: Sara Haddad (Purchasing) · sara.haddad@example.com',
        status: 'Updated on revised schedule',
        messages: [
          {
            id: 'c1',
            date: '1 Jun 2025',
            time: '04:40 PM',
            from: 'You',
            text: 'The vessel was rolled over. We are confirming the new sailing date with the agent and will update you today.',
          },
          {
            id: 'c2',
            date: '1 Jun 2025',
            time: '05:02 PM',
            from: 'Sara Haddad',
            text: 'Thank you. Please let us know as soon as it is confirmed.',
          },
          {
            id: 'c3',
            date: '3 Jun 2025',
            time: '11:30 AM',
            from: 'You',
            text: 'Confirmed: new ETD 9 June, ETA 11 June. Please confirm you are happy to proceed.',
          },
          {
            id: 'c4',
            date: '3 Jun 2025',
            time: '12:15 PM',
            from: 'Sara Haddad',
            text: 'Confirmed, please proceed with the new schedule.',
          },
        ],
      },
      'Overseas Agent': {
        contact: 'Main contact: Marco Rossi (Logistics Manager) · marco.rossi@example.com',
        status: 'Revised schedule confirmed',
        messages: [
          {
            id: 'a1',
            date: '30 May 2025',
            time: '09:12 AM',
            from: 'Marco Rossi',
            text: 'The shipment has been rolled over from Antwerp to 3 June due to schedule changes.',
          },
          {
            id: 'a2',
            date: '1 Jun 2025',
            time: '04:28 PM',
            from: 'You',
            text: 'Requested confirmation of the revised sailing date and transit time.',
          },
          {
            id: 'a3',
            date: '3 Jun 2025',
            time: '10:05 AM',
            from: 'Marco Rossi',
            text: 'Confirmed new schedule:\n• ETD: 9 June\n• ETA: 11 June\nVessel: Messina Line\nPlease proceed.',
            attachments: [
              { name: 'Schedule_MessinaLine.pdf', size: '245 KB' },
              { name: 'Booking_Confirmation.pdf', size: '180 KB' },
            ],
          },
          {
            id: 'a4',
            date: '5 Jun 2025',
            time: '11:21 AM',
            from: 'You',
            text: 'Thanked the agent and confirmed to proceed with the booking.',
          },
        ],
      },
      Accountant: {
        contact: 'Main contact: Sokaina (Accounts) · accounts@example.com',
        status: 'Awaiting final agent invoice',
        messages: [
          {
            id: 'k1',
            date: '10 Jun 2025',
            time: '09:00 AM',
            from: 'You',
            text: 'The shipment is due to arrive on 11 June. Please prepare for the agent invoice.',
          },
          {
            id: 'k2',
            date: '10 Jun 2025',
            time: '09:40 AM',
            from: 'Sokaina',
            text: 'Noted. We are waiting for the final agent invoice before we can complete the JCS.',
          },
          {
            id: 'k3',
            date: '16 Jun 2025',
            time: '10:15 AM',
            from: 'You',
            text: 'Still waiting on the agent for the final invoice. Following up today.',
          },
        ],
      },
    },
    next: [
      { text: 'Follow up on final agent invoice (Accountant)', owner: 'Accountant', due: '20 Jun 2025' },
      { text: 'Confirm vessel booking details (Agent)', owner: 'Agent', due: '' },
      { text: 'Prepare updated ETA for customer (Customer)', owner: 'Customer', due: '' },
    ],
  },
  bare('SRB-711', 'Two agent rates received. Quotation pending approval.', '14 Jul 2025', 2, true),
  bare('SRB-640', 'Customer requested updated pricing.', '10 Jul 2025', 1, true),
  bare('SRB-596', 'Customer approved revised rate with side loader.', '28 Jun 2025', 0, true),
  bare('SRB-518', 'DG approval pending. Customer extended payment terms.', '21 Apr 2025', 0, true),
  bare('SRB-625', 'Pickup confirmed by supplier.', '2 Jul 2025', 0, true),
  bare('SRB-648', 'Agent shared RJ rate. Awaiting customer confirmation.', '27 Jul 2025', 0, false),
  bare('SRB-703', 'Pre-arrival notice drafted. Waiting for the clearance broker.', '24 Jul 2025', 0, false),
  bare('SRB-701', 'Container delivered. Invoice to be sent.', '22 Jul 2025', 0, false),
  bare('SRB-512', 'Shipment closed. Final invoice paid.', '8 Jul 2025', 0, false),
]);

export const CONVERSATIONS: Conversation[] = BASE.map(withFeed);
